import { useCallback, useRef, useState } from 'react';

interface UseNavigationHoverProps {
  switchDelay?: number;
  exitDelay?: number;
}

export const useNavigationHover = ({
  switchDelay = 250,
  exitDelay = 500,
}: UseNavigationHoverProps) => {
  const [activeItem, setActiveItem] = useState('');
  const [isInViewport, setIsInViewport] = useState(false);

  const switchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const exitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const checkExitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInContentRef = useRef(false);
  const isInViewportRef = useRef(false);
  const isInTriggerRef = useRef(false);

  const clearTimeouts = useCallback(() => {
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
    if (checkExitTimeoutRef.current) {
      clearTimeout(checkExitTimeoutRef.current);
      checkExitTimeoutRef.current = null;
    }
  }, []);

  const resetNavigation = useCallback(() => {
    clearTimeouts();
    setActiveItem('');
    setIsInViewport(false);
    isInContentRef.current = false;
    isInViewportRef.current = false;
    isInTriggerRef.current = false;
  }, [clearTimeouts]);

  const checkShouldExit = () => {
    // Clear any existing check timeout
    if (checkExitTimeoutRef.current) {
      clearTimeout(checkExitTimeoutRef.current);
    }

    checkExitTimeoutRef.current = setTimeout(() => {
      // If we're not over trigger or content, start exit timer
      if (!isInTriggerRef.current && !isInContentRef.current) {
        exitTimeoutRef.current = setTimeout(() => {
          setActiveItem('');
          setIsInViewport(false);
        }, exitDelay);
      }
    }, switchDelay);
  };

  const handleTriggerEnter = (itemTitle: string) => {
    clearTimeouts();
    isInTriggerRef.current = true;

    // If no item is active, open immediately
    if (!activeItem) {
      setActiveItem(itemTitle);
      setIsInViewport(true);
      isInViewportRef.current = true;
      return;
    }

    // If same item, ensure viewport stays open
    if (activeItem === itemTitle) {
      setIsInViewport(true);
      isInViewportRef.current = true;
      return;
    }

    // Different item - delay before switching
    switchTimeoutRef.current = setTimeout(() => {
      setActiveItem(itemTitle);
      setIsInViewport(true);
      isInViewportRef.current = true;
    }, switchDelay);
  };

  const handleTriggerLeave = (e?: React.PointerEvent) => {
    if (e?.pointerType !== 'mouse') {
      return;
    }

    isInTriggerRef.current = false;

    // Cancel any pending switch
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }

    // Check if we should exit
    checkShouldExit();
  };

  const handleContentEnter = () => {
    clearTimeouts();
    isInContentRef.current = true;
    setIsInViewport(true);
    isInViewportRef.current = true;
  };

  const handleContentLeave = () => {
    isInContentRef.current = false;
    checkShouldExit();
  };

  const handleViewportEnter = () => {
    clearTimeouts();
    isInViewportRef.current = true;
    setIsInViewport(true);

    // Check if we should exit (with delay to handle transitions)
    checkShouldExit();
  };

  const handleViewportLeave = () => {
    isInViewportRef.current = false;

    // Always start exit delay when leaving viewport
    exitTimeoutRef.current = setTimeout(() => {
      setActiveItem('');
      setIsInViewport(false);
    }, exitDelay);
  };

  return {
    resetNavigation,
    activeItem,
    setActiveItem,
    isInViewport,
    handleTriggerEnter,
    handleTriggerLeave,
    handleContentEnter,
    handleContentLeave,
    handleViewportEnter,
    handleViewportLeave,
  };
};
